/**
 * Utility functions for determining highest-tier drivers based on COMP_SELECTION_CONFIG
 */

// Note: This will need to be dynamically imported or copied since it's a Node.js module
// For now, we'll duplicate the tier structure here for client-side use
const TIER_CONFIG = {
    tier1: {
        positiveDrivers: [
            "waterfront_frontage_prime",
            "golf_frontage_prime"
        ],
        negativeDrivers: [
            "separation_crosses_major_highway_artery",
            "attached_cluster_micro_lot",
            "adjacent_heavy_commercial_or_parking",
            "adjacent_industrial_or_utility_site",
            "overhead_high_voltage_corridor",
            "terrain_hazard_slope_or_cliff",
            "noise_rating_high"
        ]
    },
    tier2: {
        positiveDrivers: [
            "waterfront_frontage_secondary",
            "golf_frontage_nonprime",
            "adjacent_park_or_greenbelt",
            "backs_to_natural_open_space",
            "broad_mountain_view"
        ],
        negativeDrivers: [
            "separation_crosses_navigable_river_or_creek",
            "flood_control_basin_or_water_hazard",
            "backs_to_engineered_canal_or_drainage",
            "nearby_ag_or_industrial_operations",
            "backs_to_multifamily"
        ]
    },
    tier3: {
        positiveDrivers: [
            "waterfront_view_only",
            "nearby_park_or_greenbelt",
            "partial_mountain_view",
            "extra_lot_privacy_or_buffer",
            "cul_de_sac_setting"
        ],
        negativeDrivers: [
            "separation_crosses_active_rail_line",
            "visual_blight_billboards_or_wires",
            "flag_lot_setting",
            "access_constraint_shared_easement",
            "access_constraint_limited_parking_or_grade",
            "t_intersection_head_on"
        ]
    }
};

/**
 * Find the highest-tier driver from a drivers array
 * Checks tier1 first, then tier2, then tier3
 * Returns the first matching driver from the highest tier found
 * 
 * @param {string[]} drivers - Array of driver enum values
 * @param {boolean} isPositive - Whether to check positive or negative drivers
 * @returns {string|null} - The highest-tier driver found, or null if none match
 */
export const getHighestTierDriver = (drivers, isPositive = true) => {
    if (!Array.isArray(drivers) || drivers.length === 0) {
        return null;
    }

    const tierKey = isPositive ? 'positiveDrivers' : 'negativeDrivers';
    
    // Check tier1 first
    const tier1Drivers = TIER_CONFIG.tier1[tierKey];
    for (const driver of drivers) {
        if (tier1Drivers.includes(driver)) {
            return driver;
        }
    }

    // Check tier2
    const tier2Drivers = TIER_CONFIG.tier2[tierKey];
    for (const driver of drivers) {
        if (tier2Drivers.includes(driver)) {
            return driver;
        }
    }

    // Check tier3
    const tier3Drivers = TIER_CONFIG.tier3[tierKey];
    for (const driver of drivers) {
        if (tier3Drivers.includes(driver)) {
            return driver;
        }
    }

    // No match found in any tier
    return null;
};

/**
 * Get the highest-tier positive and negative drivers from curb appeal data
 * 
 * @param {Object} curbAppealData - Object with positiveDrivers, negativeDrivers arrays, and optionally noiseRating
 * @returns {Object} - Object with topPositive and topNegative driver strings (or null)
 */
export const getTopDrivers = (curbAppealData) => {
    if (!curbAppealData) {
        return { topPositive: null, topNegative: null };
    }

    const positiveDrivers = Array.isArray(curbAppealData.positiveDrivers) 
        ? curbAppealData.positiveDrivers 
        : [];
    
    // Build negative drivers array, including noiseRating if it's "high"
    let negativeDrivers = Array.isArray(curbAppealData.negativeDrivers) 
        ? [...curbAppealData.negativeDrivers] 
        : [];
    
    // Add noise_rating_high as a tier 1 negative driver if noiseRating is "high"
    if (curbAppealData.noiseRating && 
        typeof curbAppealData.noiseRating === 'string' && 
        curbAppealData.noiseRating.toLowerCase() === 'high') {
        negativeDrivers.push("noise_rating_high");
    }

    return {
        topPositive: getHighestTierDriver(positiveDrivers, true),
        topNegative: getHighestTierDriver(negativeDrivers, false)
    };
};

